import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { emailIsValid } from '@/components/signup/utils/email-is-valid';
import { isPersonalDomain } from '@/components/signup/utils/is-personal-domain';

import { firePersonalEmailEnteredAnalytics } from './personal-email-entered-analytics';

/**
 * Tracks how many users enter a personal-domain email on the signup form,
 * independent of the `spark_recommend_work_email_signup` experiment.
 *
 * Unlike {@link useRecommendWorkEmailNudge}, this hook does NOT read the
 * experiment and does NOT gate on enrollment, control, or treatment. It fires
 * for every user who types a complete personal-domain email, providing the
 * experiment-independent baseline population of personal-email signups.
 *
 * The `signupPersonalEmail entered` track event fires at most once per mount,
 * the first time the user has entered a *valid* email address on a known
 * personal domain (e.g. "ada@gmail.com"). Requiring a complete, valid email
 * keeps this baseline aligned with the data-side `signup_domain_type = 'PERSONAL'`
 * classification and avoids counting partially-typed or malformed input.
 *
 * @param email The current email input value (may be partially typed).
 */
export const usePersonalEmailEntered = ({ email }: { email: string }): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// One-shot guard: only the first transition into "valid personal email
	// entered" is recorded, so later edits (or clearing the field) do not re-fire.
	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (!hasFiredRef.current && emailIsValid(email) && isPersonalDomain(email)) {
			hasFiredRef.current = true;
			firePersonalEmailEnteredAnalytics({ createAnalyticsEvent });
		}
	}, [email, createAnalyticsEvent]);
};
